import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import TicketDetails from "../components/TicketDetails";
import Layout from "../components/Layout/Layout";
import TicketForm from "../components/TicketForm";
import { PlusIcon } from "@heroicons/react/24/solid"; // or /outline for outline version
import { XMarkIcon } from "@heroicons/react/24/solid";
import Select from "react-select";
import Dashboard from "./Dashboard";

import api from "../utils/api";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
} from "chart.js";
ChartJS.register(
  ArcElement,
  BarElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale
);
import TicketDashboardSummary from "../components/TicketDashboardSummary";
import TicketTable from "../components/TicketTable";
import Loader from "../components/Loader";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";
import {
  getStatusColor,
  getStatusTextColor,
  getStatusIcon,
  getStatusLabel,
} from "../utils/statusStyles";
import { useUser } from "../contexts/UserContext";
import { useClient } from "../contexts/ClientContext";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import TicketSchedulerTable from "../components/TicketScheduler/TicketSchedulerTable";
import TicketSchedulerModal from "../components/TicketScheduler/TicketSchedulerModal";

import { format } from "date-fns";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ArrowPathIcon } from "@heroicons/react/24/solid";

export default function TicketListPage() {
  const { user } = useUser();
  const { selectedClientId, setSelectedClientId } = useClient();
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [selectedTicketChildId, setSelectedTicketChildId] = useState(null);
  const [ticketViewIndex, setTicketViewIndex] = useState(-1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const saved = localStorage.getItem("ticketRowsPerPage");
    return saved ? parseInt(saved, 10) : 10;
  });
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);
  const [minTicketDate, setMinTicketDate] = useState(null);
  const [maxTicketDate, setMaxTicketDate] = useState(null);
  const safeMinDate = minTicketDate ?? new Date(new Date().getFullYear() - 1, 0, 1);
  const safeMaxDate = maxTicketDate ?? new Date();
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [userStatus, setUserStatus] = useState(null);
  const searchInputRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalTickets, setTotalTickets] = useState(0);
  const [statusSummaryData, setStatusSummaryData] = useState([]);
  const [userStatusSummaryData, setUserStatusSummaryData] = useState({ waiting: 0, open: 0, mytickets: 0 });
  const [allModules, setAllModules] = useState([]);
  const [filterDate, setFilterDate] = useState("");
  const [priority, setPriority] = useState([]);
  const [module, setModule] = useState([]);
  const [showBadges, setShowBadges] = useState(true);
  const [activeTab, setActiveTab] = useState("all"); // "all" or "scheduler"
  const [showSchedulerModal, setShowSchedulerModal] = useState(false);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [schedulerRefreshKey, setSchedulerRefreshKey] = useState(0);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ticketIdFromUrl = searchParams.get("tid");

  const handleTicketClick = useCallback((ticket) => {
    setSelectedTicketId(ticket?.id);
    setSelectedTicketChildId(ticket?.child_id);
    const idx = tickets.findIndex(t => t.id === ticket?.id);
    setTicketViewIndex(idx);
  }, [tickets]);

  const closeDetails = useCallback(() => {
    setSelectedTicketId(null);
    setSelectedTicketChildId(null);
    setTicketViewIndex(-1);
  }, []);

  const handleNextTicket = useCallback(() => {
    const nextIdx = Math.min(tickets.length - 1, ticketViewIndex + 1);
    if (nextIdx !== ticketViewIndex) {
      const nextTicket = tickets[nextIdx];
      setSelectedTicketId(nextTicket.id);
      setSelectedTicketChildId(nextTicket.child_id);
      setTicketViewIndex(nextIdx);
    }
  }, [tickets, ticketViewIndex]);

  const handlePrevTicket = useCallback(() => {
    const prevIdx = Math.max(0, ticketViewIndex - 1);
    if (prevIdx !== ticketViewIndex) {
      const prevTicket = tickets[prevIdx];
      setSelectedTicketId(prevTicket.id);
      setSelectedTicketChildId(prevTicket.child_id);
      setTicketViewIndex(prevIdx);
    }
  }, [tickets, ticketViewIndex]);

  useEffect(() => {
    document.title = "Enova E - Ticketing";
  }, []);

  useEffect(() => {
    localStorage.setItem("ticketRowsPerPage", String(rowsPerPage));
  }, [rowsPerPage]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    if (searchInputRef.current && user?.role_id == 2) {
      searchInputRef.current.focus();
    }
  }, []);

  // Load paginated dataset; searching, filtering and pagination are now server-side.
  const loadTickets = useCallback(
    async (page = 1, currentRowsPerPage = rowsPerPage, signal) => {
      setIsLoading(true);
      try {
        const offset = (page - 1) * currentRowsPerPage;
        const res = await api.get("/ticket", {
          params: {
            child_id: user?.child_id ?? "",
            limit: currentRowsPerPage,
            offset: offset,
            search: debouncedSearch,
            statusId: selectedStatus || "",
            userStatus: userStatus || "",
            clientId: selectedClientId || "",
            priorityId: priority.join(','),
            module: module.join(','),
            fromDate: fromDate ? format(fromDate, "yyyy-MM-dd") : "",
            toDate: toDate ? format(toDate, "yyyy-MM-dd") : "",
          },
          ...(signal && { signal }),
        });
        const data = res.data;
        const raw = (data?.data != null)
          ? (Array.isArray(data.data) ? data.data : [data.data])
          : [];
        if (data && data.status === true) {
          const mapped = raw.map((t) => {
            return {
              id: t.id,
              collegeName: t.college_name,
              client: t.client_name,
              client_key: t.client_key,
              module: t.module_name,
              title: t.title,
              description: t.description,
              priority: t.priority,
              status: t.status,
              status_id: t.status_id,
              assign_user_id: t.assign_user_id,
              created_by_user_id: t.created_by_user_id,
              assignedTo: t.assigned_to,
              AssignedTo_designation_id: t.AssignedTo_designation_id,
              ReportedBy_designation_id: t.ReportedBy_designation_id,
              created: t.created_date,
              updated: t.updated_date,
              involves_user: t.involves_user,
              comment_count: t.comment_count,
              child_id: t.child_id,
              issue_type: t.issue_type,
              customer_name: t.customer_name,
              priority_id: t.priority_id,
              module_id: t.module,
            };
          });
          setTickets(mapped);
          setTotalTickets(data.total || mapped.length);

          // One-time setup for date range if not already set or for initial load
          if (!fromDate && !toDate && mapped.length > 0) {
            const dates = mapped.map((t) => t.created).filter(Boolean);
            if (dates.length) {
              setMinTicketDate(new Date(Math.min(...dates.map((d) => new Date(d)).map(Number))));
              setMaxTicketDate(new Date(Math.max(...dates.map((d) => new Date(d)).map(Number))));
            }
          }
        } else {
          setTickets([]);
          setTotalTickets(0);
        }
      } catch (err) {
        if (err?.code === "ERR_CANCELED" || err?.name === "AbortError") return;
        console.error("Fetch failed:", err);
      } finally {
        setIsLoading(false);
      }
    },
    [user?.child_id, rowsPerPage, debouncedSearch, selectedStatus, userStatus, selectedClientId, priority, module, fromDate, toDate]
  );

  const loadSummaries = useCallback(async () => {
    try {
      const [statusRes, userRes] = await Promise.all([
        api.get("/ticket/status-summary", {
          params: {
            child_id: user?.child_id ?? "",
            clientId: selectedClientId || ""
          }
        }),
        api.get("/ticket/user-status-summary", {
          params: {
            child_id: user?.child_id ?? "",
            clientId: selectedClientId || ""
          }
        })
      ]);

      if (statusRes.data?.status) {
        let data = statusRes.data.data;
        // For customers, merge all testing counts (7-11) into "IN process" (Status 4)
        if (Number(user?.role_id) === 2) {
          const inProcessStat = data.find(s => Number(s.status_id) === 4);
          const testingCount = data
            .filter(s => Number(s.status_id) >= 7 && Number(s.status_id) <= 11)
            .reduce((sum, s) => sum + Number(s.count || 0), 0);

          if (inProcessStat) {
            inProcessStat.count = Number(inProcessStat.count) + testingCount;
          }
          data = data.filter(s => Number(s.status_id) <= 6);
        }
        setStatusSummaryData(data);
      }
      if (userRes.data?.status) {
        setUserStatusSummaryData(userRes.data.data);
      }
    } catch (err) {
      console.error("Failed to load summaries:", err);
    }
  }, [user?.child_id, selectedClientId]);

  const loadModules = useCallback(async () => {
    try {
      const res = await api.get("/ticket/get-modules", {
        params: { child_id: user?.child_id ?? "" }
      });
      if (res.data?.status) {
        setAllModules(res.data.data);
      }
    } catch (err) {
      console.error("Failed to load modules:", err);
    }
  }, [user?.child_id]);

  useEffect(() => {
    if (!user) return;
    loadSummaries();
    loadModules();
  }, [loadSummaries, loadModules, user]);

  useEffect(() => {
    if (!user) return;
    const controller = new AbortController();
    loadTickets(currentPage, rowsPerPage, controller.signal);
    return () => controller.abort();
  }, [loadTickets, user, currentPage, rowsPerPage]);

  const userId = user?.id ?? user?.user_id ?? null;
  const roleId = Number(user?.role_id) || 0;

  const filteredTickets = useMemo(() => {
    return tickets;
  }, [tickets]);


  const statusSummary = useMemo(() => {
    return statusSummaryData.map((s) => ({
      status_id: Number(s.status_id),
      label: getStatusLabel(Number(s.status_id), s.status_name),
      count: Number(s.count),
      color: getStatusColor(Number(s.status_id)),
      text: getStatusTextColor(Number(s.status_id)),
      icon: getStatusIcon(Number(s.status_id)),
    }));
  }, [statusSummaryData]);

  const userStatusCounts = useMemo(() => {
    return userStatusSummaryData;
  }, [userStatusSummaryData]);




  const collegeName = useMemo(() => {
    if (!selectedClientId || !tickets.length) return " - Overall";
    const first = tickets.find((t) => t.client_key === selectedClientId);
    if (!first) return " - Overall";
    return " - " + String((first.client || "") + " (" + (first.collegeName || "") + ")").toUpperCase();
  }, [tickets, selectedClientId]);

  // Static options to ensure dropdown is never empty
  const STATIC_PRIORITY_OPTIONS = [
    { value: "1", label: "Critical" },
    { value: "2", label: "High" },
    { value: "3", label: "Medium" },
    { value: "7", label: "Low" },
    { value: "4", label: "To Be Done In Future" },
  ];

  const priorityOptions = useMemo(() => {
    return STATIC_PRIORITY_OPTIONS;
  }, []);

  const moduleOptions = useMemo(() => {
    return allModules.map((m) => ({
      value: String(m.id),
      label: m.module_name,
    }));
  }, [allModules]);

  const statusCounts = useMemo(
    () => ({
      total: totalTickets,
      open: 0,
      resolved: 0,
      canceled: 0
    }),
    [totalTickets]
  );

  const handleFilterClick = useCallback((key) => {
    setSearch("");
    setModule([]);
    setPriority([]);
    setFromDate(null);
    setToDate(null);
    setSelectedStatus(null);
    setUserStatus((prev) => (prev === key ? null : key));
    setCurrentPage(1);
  }, []);

  const applyFilter = useCallback(({ status = null, user = null }) => {
    setSearch("");
    setModule([]);
    setPriority([]);
    setFromDate(null);
    setToDate(null);
    setSelectedStatus(status);
    setUserStatus(user);
    setCurrentPage(1);
  }, []);

  const statusOptions = [
    { value: "1", label: "New" },
    { value: "4", label: "IN process" },
    { value: "3", label: "Pending verification & closure" },
    { value: "5", label: "Closed" },
    { value: "6", label: "Reopen" },
    { value: "8", label: "Testing" },
    { value: "9", label: "Testing Approved" },
    { value: "10", label: "Testing Rejected" },
    { value: "12", label: "Pending Customer Verification & Confirmation" },
  ];

  useEffect(() => {
    if (tickets.length > 0 && ticketIdFromUrl) {
      const matched = tickets.find(
        (t) => String(t.id).trim() === String(ticketIdFromUrl).trim()
      );

      if (matched) {
        handleTicketClick(matched);

        // Remove the tid param from URL
        navigate("/tickets", { replace: true });
      }
    }
  }, [tickets, ticketIdFromUrl]);

  if (!user) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center text-lg text-gray-600">
        Loading user information...
      </div>
    );
  }

  return (
    <Layout noSidebar={Number(user?.role_id) !== 2 ? false : true}>
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="p-2 bg-neutral-light min-h-full relative ">
        <div className="bg-white rounded-xl shadow px-3 py-2">
          <div className="w-full flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
            {/* Title */}
            <div className="flex flex-col sm:flex-col lg:flex-row lg:items-center gap-2 flex-1">
              <div className="flex items-center justify-between">
                <h2 className="text-base sm:text-md md:text-lg xl:text-xl font-medium  truncate">
                  Ticket Dashboard {collegeName}
                </h2>

                <button
                  onClick={() => setShowBadges(!showBadges)}
                  className="sm:hidden text-sm text-primary underline flex items-center gap-1"
                >
                  {showBadges ? <FiChevronUp /> : <FiChevronDown />}
                </button>
              </div>

              {/* Tabs Section - Admin Only */}
              {Number(user?.role_id) === 1 && (
                <div className="flex bg-gray-100/50 p-1 rounded-lg w-fit">
                  <button
                    onClick={() => setActiveTab("all")}
                    className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-all ${activeTab === 'all'
                      ? 'bg-white text-red-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                      }`}
                  >
                    All Tickets
                  </button>
                  <button
                    onClick={() => setActiveTab("scheduler")}
                    className={`px-4 py-1.5 rounded-md text-sm font-semibold transition-all ${activeTab === 'scheduler'
                      ? 'bg-white text-red-600 shadow-sm'
                      : 'text-gray-500 hover:text-gray-700'
                      }`}
                  >
                    Ticket Scheduler
                  </button>
                </div>
              )}
            </div>
          </div>

          {activeTab === 'all' ? (
            <>

              <TicketDashboardSummary
                statusSummary={statusSummary}
                statusCounts={statusCounts}
                onStatusClick={(statusId) => applyFilter({ status: statusId })}
                selectedStatusId={selectedStatus}
                userStatus={userStatus}
              />

              {/* Buttons */}
              <div className="flex flex-wrap justify-between items-start gap-3 mb-3 mt-3">
                <div
                  className={`flex flex-wrap gap-2  ${showBadges ? "" : "hidden"} `}
                >
                  {[
                    {
                      label: "Waiting on Me",
                      count: userStatusCounts.waiting,
                      color: " text-yellow-800",
                      key: "waiting",
                      note: "Tickets assigned to you that require your action.",
                    },
                    {
                      label: "Open Tickets",
                      count: userStatusCounts.open,
                      color: " text-orange-800",
                      key: "open",
                      note: "Tickets that are new, in progress, or reopened.",
                    },
                    {
                      label: "My Tickets",
                      count: userStatusCounts.mytickets,
                      color: " text-blue-800",
                      key: "mytickets",
                      note: "Tickets you created, updated, or are assigned to.",
                    },
                  ].map(({ label, count, color, key, note }) => {
                    const isActive = userStatus === key;
                    return (
                      <div key={key} className="relative group">
                        <button
                          onClick={() => handleFilterClick(key)}
                          className={`px-2 py-1.5 rounded-full font-medium text-textPrimary text-sm flex items-center gap-2 transition border whitespace-nowrap justify-center md:justify-start ${color}
          ${isActive ? "bg-main text-white" : "hover:shadow-md"}`}
                        >
                          <span>{label}</span>
                          <span className="bg-white text-black px-2 py-0.5 rounded-full text-xs font-semibold">
                            {count}
                          </span>
                        </button>

                        {/* Tooltip */}
                        <div className="absolute bottom-full mb-2 hidden group-hover:block w-48 text-sm text-white bg-gray-900 rounded-lg px-3 py-2 shadow-lg">
                          {note}
                          <div className="absolute left-4 top-full w-2 h-2 bg-gray-900 rotate-45"></div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="flex flex-wrap gap-2 justify-end w-full md:w-auto">
                  <button
                    className="bg-red-700 hover:bg-red-800 text-sm text-white px-2 py-1 rounded-md transition font-medium whitespace-nowrap inline-flex items-center gap-2"
                    onClick={() => setShowForm(true)}
                  >
                    <PlusIcon className="w-5 h-5 text-white-200 " />
                    Create Ticket
                  </button>

                  <button
                    onClick={() => loadTickets()}
                    className=" text-sm px-4 py-1 rounded-md transition font-medium whitespace-nowrap inline-flex items-center gap-2 border  "
                  >
                    <ArrowPathIcon className="w-4 h-4" />
                    <span className="hidden sm:inline ml-1">Refresh</span>
                  </button>
                </div>
              </div>

              <div className="mt-3 mb-6">
                <div className="grid grid-cols-2 md:grid-cols-4 lg:flex lg:flex-wrap items-end gap-x-3 gap-y-4">
                  {/* Rows Per Page - Full width on mobile for better separation */}
                  <div className="col-span-2 sm:col-span-1 lg:w-[100px]">
                    <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1 ml-1">Rows per page</label>
                    <select
                      value={rowsPerPage || 10}
                      onChange={(e) => setRowsPerPage(Number(e.target.value))}
                      className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 w-full bg-white shadow-sm h-[38px]"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={99999}>All</option>
                    </select>
                  </div>

                  {/* From Date - Half width on mobile */}
                  <div className="col-span-1 lg:w-[140px]">
                    <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1 ml-1">From Date</label>
                    <DatePicker
                      popperProps={{ strategy: "fixed" }}
                      popperClassName="z-50"
                      selected={fromDate}
                      onChange={(date) => setFromDate(date)}
                      placeholderText="Date →"
                      dateFormat="dd/MM/yyyy"
                      minDate={safeMinDate}
                      maxDate={toDate || safeMaxDate}
                      isClearable
                      className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-red-500 shadow-sm h-[38px]"
                    />
                  </div>

                  {/* To Date - Half width on mobile */}
                  <div className="col-span-1 lg:w-[140px]">
                    <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1 ml-1">To Date</label>
                    <DatePicker
                      popperProps={{ strategy: "fixed" }}
                      popperClassName="z-50"
                      selected={toDate}
                      onChange={(date) => setToDate(date)}
                      placeholderText="Date ←"
                      dateFormat="dd/MM/yyyy"
                      minDate={fromDate || safeMinDate}
                      maxDate={safeMaxDate}
                      isClearable
                      className="border border-gray-300 rounded px-3 py-1.5 text-sm w-full focus:outline-none focus:ring-2 focus:ring-red-500 shadow-sm h-[38px]"
                    />
                  </div>

                  {/* Module Filter - Full width on mobile */}
                  <div className="col-span-2 lg:w-[220px]">
                    <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1 ml-1">Module</label>
                    <Select
                      isMulti
                      name="modules"
                      options={moduleOptions}
                      classNamePrefix="select"
                      className="text-sm shadow-sm"
                      value={moduleOptions.filter((o) => module.includes(o.value))}
                      onChange={(selected) => setModule(selected.map((o) => o.value))}
                      placeholder="All Modules"
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: '38px',
                          borderRadius: '4px',
                          borderColor: '#d1d5db',
                        })
                      }}
                    />
                  </div>

                  {/* Priority Filter - Full width on mobile */}
                  <div className="col-span-2 lg:w-[180px]">
                    <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1 ml-1">Priority</label>
                    <Select
                      isMulti
                      name="priority"
                      options={priorityOptions}
                      classNamePrefix="select"
                      className="text-sm shadow-sm"
                      value={priorityOptions.filter((o) => priority.includes(o.value))}
                      onChange={(selected) => setPriority(selected.map((o) => String(o.value)))}
                      placeholder="All"
                      styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: '38px',
                          borderRadius: '4px',
                          borderColor: '#d1d5db',
                        })
                      }}
                    />
                  </div>

                  {/* Search Filter - Full width on mobile */}
                  <div className="col-span-2 lg:flex-grow lg:max-w-xs relative">
                    <label className="block text-[10px] uppercase font-bold text-gray-500 mb-1 ml-1">Search</label>
                    <div className="relative">
                      <input
                        ref={searchInputRef}
                        type="text"
                        className="border border-gray-300 rounded-lg px-4 py-2 pr-10 w-full focus:outline-none focus:ring-2 focus:ring-red-500 shadow-sm transition-all h-[38px] text-sm"
                        placeholder="ID, Title, Status..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                      {search && (
                        <button
                          onClick={() => {
                            setSearch("");
                            searchInputRef.current?.focus();
                          }}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 flex items-center justify-center text-white bg-red-600 rounded-full text-xs shadow-sm transition hover:bg-red-700"
                          aria-label="Clear search"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <TicketTable
                tickets={filteredTickets}
                onTicketClick={handleTicketClick}
                isLoading={isLoading}
                rowsPerPage={rowsPerPage}
                totalRows={totalTickets}
                currentPage={currentPage}
                onPageChange={(page) => setCurrentPage(page)}
                onRowsPerPageChange={(newRows) => {
                  setRowsPerPage(newRows);
                  setCurrentPage(1);
                }}
              />

              {selectedTicketId && (
                <TicketDetails
                  ticketId={selectedTicketId}
                  onClose={closeDetails}
                  priorityOptions={priorityOptions}
                  statusOptions={statusOptions}
                  userInfo={user}
                  selectedClientId={selectedClientId}
                  selectedTicketChildId={selectedTicketChildId}
                  onSuccess={() => {
                    setShowForm(false);
                    toast.success("Ticket updated successfully");
                    loadTickets();
                    loadSummaries();
                  }}
                  onNext={handleNextTicket}
                  onPrevious={handlePrevTicket}
                  hasNext={ticketViewIndex < tickets.length - 1}
                  hasPrevious={ticketViewIndex > 0}
                />
              )}
            </>
          ) : null}
        </div>

        {activeTab === 'scheduler' && (
          <div className="mt-4">
            <TicketSchedulerTable
              key={schedulerRefreshKey}
              onEdit={(schedule) => {
                setSelectedSchedule(schedule);
                setShowSchedulerModal(true);
              }}
            />
          </div>
        )}

        {showSchedulerModal && (
          <TicketSchedulerModal
            schedule={selectedSchedule}
            onClose={() => {
              setShowSchedulerModal(false);
              setSelectedSchedule(null);
            }}
            onSave={() => {
              setSchedulerRefreshKey(prev => prev + 1);
            }}
          />
        )}

        {showForm && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30 backdrop-blur-sm px-4"
          >
            <div
              className="relative w-full max-w-4xl bg-white rounded-[20px] shadow-2xl overflow-hidden flex flex-col max-h-[95vh]"
              onClick={(e) => e.stopPropagation()} // stop closing when clicking inside
            >
              {/* Close Button */}
              <button
                onClick={() => setShowForm(false)}
                className="absolute top-5 right-5 w-6 h-6 bg-black text-white rounded-full flex items-center justify-center hover:bg-gray-200 hover:text-black transition"
                aria-label="Close"
              >
                <XMarkIcon className="w-4 h-4" />
              </button>

              {/* Header */}
              <div className="p-6 border-b shrink-0">
                <h2 className="text-2xl font-semibold text-center text-red-600">
                  Create New Ticket
                </h2>
                <p className="text-sm text-center text-gray-500 mt-1">
                  Fill in the details below to raise a support request.
                </p>
              </div>

              {/* Form */}
              <div className="px-6 py-4 space-y-6 flex-1 overflow-y-auto">
                <TicketForm
                  onSuccess={() => {
                    toast.success("Ticket created successfully");
                    loadTickets();
                    loadSummaries();
                    setShowForm(false);
                  }}
                  isAdmin={user?.role_id !== "2"}
                  priorityOptions={priorityOptions}
                  statusOptions={statusOptions}
                  userInfo={user}
                  selectedClientId={selectedClientId}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
